/**
 * "Customise" — show, hide and reorder the dashboard's sections.
 *
 * A portfolio operator and a single-property manager want different things at
 * the top of this page, and neither wants to scroll past the other's. The
 * choice is stored per portal user in `user_preferences`.
 *
 * It is a disclosure, not a dialog: the panel is inline, Escape closes it and
 * returns focus to the button, and a click outside dismisses it. Reordering is
 * two buttons per row rather than drag-and-drop, because a drag handle is
 * unusable by keyboard and on a phone without a great deal of machinery.
 *
 * It deliberately does NOT close when focus leaves. That was tried: the browser
 * moves focus to <main> during re-render often enough that the panel dismissed
 * itself mid-interaction, and a control that vanishes while you are using it is
 * worse than one that waits for Escape or a click.
 */
import { useId, useRef, useState } from 'react';
import { useDismissable } from '@pazportal/ui';
import { useI18n } from '../i18n/I18nProvider';
import type { MessageKey } from '../i18n/messages';
import { useUserPreferences, type DashboardWidget } from '../hooks/useUserPreferences';

const WIDGET_LABEL: Record<DashboardWidget, MessageKey> = {
  metrics: 'dashboard.widget.metrics',
  sites: 'dashboard.sites_title',
  alerts: 'dashboard.alerts_open',
  systems: 'dashboard.installed_platforms',
  health: 'dashboard.operational_health',
  trips: 'dashboard.widget.trips',
  activity: 'dashboard.recent_activity',
};

export function DashboardLayoutMenu() {
  const { t } = useI18n();
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { layout, isHidden, toggleWidget, moveUp, moveDown, reset, readOnly } = useUserPreferences();

  useDismissable(containerRef, open, () => setOpen(false), buttonRef);

  if (readOnly) return null;

  const hiddenCount = layout.hidden.length;

  return (
    <div
      ref={containerRef}
      className="relative"
    >
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        className="inline-flex min-h-11 items-center gap-1.5 rounded-md border border-border bg-surface px-3 text-sm text-text-muted hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:min-h-9"
      >
        <svg aria-hidden="true" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h10" />
        </svg>
        {t('dashboard.customise')}
        {hiddenCount > 0 && (
          <span className="rounded-full bg-surface-3 px-1.5 text-[11px] font-semibold text-text-muted">
            {t('dashboard.customise_hidden', { n: hiddenCount })}
          </span>
        )}
      </button>

      {open && (
        <div
          id={panelId}
          className="absolute right-0 z-50 mt-2 w-[min(20rem,calc(100vw-2rem))] rounded-lg border border-border bg-surface p-2 shadow-lg"
        >
          <div className="flex items-center justify-between gap-2 border-b border-divider px-2 pb-2">
            <h2 className="text-sm font-medium text-text">{t('dashboard.customise_title')}</h2>
            <button
              type="button"
              onClick={reset}
              className="rounded-sm px-2 py-1 text-xs font-medium text-primary hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {t('dashboard.customise_reset')}
            </button>
          </div>

          <ul className="mt-1">
            {layout.order.map((widget, index) => {
              const hidden = isHidden(widget);
              const label = t(WIDGET_LABEL[widget]);
              return (
                <li key={widget} className="flex items-center gap-1 rounded-md px-1 py-0.5 hover:bg-surface-2">
                  {/* The label points at the input rather than wrapping it. A
                      wrapping label forwards a click on the box to the box
                      again, so a real pointer click toggled twice and appeared
                      to do nothing. */}
                  <input
                    id={`${panelId}-${widget}`}
                    type="checkbox"
                    checked={!hidden}
                    onChange={() => toggleWidget(widget)}
                    className="h-4 w-4 shrink-0 accent-[color:var(--primary)]"
                  />
                  <label
                    htmlFor={`${panelId}-${widget}`}
                    className="flex min-h-11 flex-1 cursor-pointer items-center text-sm text-text sm:min-h-9"
                  >
                    <span className={hidden ? 'text-text-muted line-through' : ''}>{label}</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => moveUp(widget)}
                    disabled={index === 0}
                    aria-label={t('dashboard.customise_move_up', { name: label })}
                    className="rounded-sm p-1.5 text-text-muted hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-30"
                  >
                    <svg aria-hidden="true" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => moveDown(widget)}
                    disabled={index === layout.order.length - 1}
                    aria-label={t('dashboard.customise_move_down', { name: label })}
                    className="rounded-sm p-1.5 text-text-muted hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-30"
                  >
                    <svg aria-hidden="true" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </li>
              );
            })}
          </ul>
          <p className="border-t border-divider px-2 pt-2 text-[11px] text-text-faint">
            {t('dashboard.customise_hint')}
          </p>
        </div>
      )}
    </div>
  );
}
